import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';

// Painel de adoção do Vendas Web.
//
// A pergunta que a tela responde é uma só: quem já virou para a plataforma
// nova e quem ainda lança pelo TradOrca. Por isso tudo aqui é comparação entre
// os dois — não há métrica de venda, só de origem do pedido.

const NOVO = 'Vendas Web';
const LEGADO = 'TradOrca';

const ESTAGIO = {
  migrada:      { rotulo: 'migrada',      cor: 'var(--vw-ok)' },
  virando:      { rotulo: 'virando',      cor: 'var(--vw-meio)' },
  comecando:    { rotulo: 'começando',    cor: 'var(--vw-inicio)' },
  'nao-comecou':{ rotulo: 'não começou',  cor: 'var(--vw-parado)' },
  'sem-pedidos':{ rotulo: 'sem pedidos',  cor: 'var(--vw-parado)' },
};

const num = (n) => Number(n || 0).toLocaleString('pt-BR');
const pct = (n) => (n === null || n === undefined ? '—' : `${Number(n).toFixed(1)}%`);

// Cores das lojas. Oito tons distinguíveis entre si e no escuro — a linha de
// cada loja precisa ser rastreável no meio das outras.
const CORES_LOJA = [
  '#3fb27f', '#e0a33e', '#5b9bd5', '#c2596d',
  '#8e79c9', '#4bb3c3', '#d1785a', '#7f9a4e',
];
const CINZA = '#6b7280';

// Evolução mensal. Com uma loja escolhida no filtro desenha só a linha dela;
// sem filtro, uma linha por loja mais a linha do total.
//
// Os chips embaixo focam lojas: sem nenhum marcado todas aparecem em cor cheia
// (é o estado de leitura geral); ao marcar a primeira, as demais recuam para
// cinza e servem de contexto em vez de sumirem — comparar duas lojas contra o
// pano de fundo das outras é o que dá sentido ao número.
function Evolucao({ serie, seriesPorLoja }) {
  const [ativo, setAtivo] = useState(null);
  const [focadas, setFocadas] = useState(() => new Set());
  const svgRef = useRef(null);

  const L = 760;
  const A = 230;
  const padE = 36;
  const padD = 12;
  const padT = 14;
  const padB = 26;

  if (serie.length < 2) return <p className="text-muted small">Sem série suficiente.</p>;

  const x = (i) => padE + (i * (L - padE - padD)) / (serie.length - 1);
  const y = (v) => padT + ((100 - (v ?? 0)) * (A - padT - padB)) / 100;

  const caminho = (pontos) => pontos
    .map((p, i) => (p.adocao === null ? null : `${i === 0 || pontos[i - 1].adocao === null ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.adocao).toFixed(1)}`))
    .filter(Boolean).join(' ');

  const multi = (seriesPorLoja || []).length > 0;
  const linhaTotal = caminho(serie);
  const area = `${linhaTotal} L${x(serie.length - 1).toFixed(1)},${A - padB} L${x(0).toFixed(1)},${A - padB} Z`;

  const temFoco = focadas.size > 0;
  const alternar = (filial) => setFocadas((antes) => {
    const proximo = new Set(antes);
    if (proximo.has(filial)) proximo.delete(filial);
    else proximo.add(filial);
    return proximo;
  });

  const linhas = multi
    ? seriesPorLoja.map((sl, i) => {
      const cor = CORES_LOJA[i % CORES_LOJA.length];
      const emFoco = !temFoco || focadas.has(sl.filial);
      return { ...sl, cor, emFoco, d: caminho(sl.pontos) };
    })
    : [];

  // Cinzas primeiro, destacadas por cima: sem isso a linha em foco fica
  // escondida atras das outras.
  const ordenadas = [...linhas].sort((a, b) => Number(a.emFoco) - Number(b.emFoco));

  const aoMover = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const r = svg.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * L;
    const i = Math.round(((px - padE) / (L - padE - padD)) * (serie.length - 1));
    setAtivo(Math.min(Math.max(i, 0), serie.length - 1));
  };

  // Com lojas em foco o tooltip mostra so elas: e o que a pessoa escolheu
  // comparar, e listar as oito de novo desfaria o recorte.
  const noMes = ativo === null ? [] : linhas
    .filter((l) => !temFoco || focadas.has(l.filial))
    .map((l) => ({ nome: l.nome, cor: l.cor, ...l.pontos[ativo] }))
    .filter((p) => p.adocao !== null)
    .sort((a, b) => b.adocao - a.adocao);

  return (
    <div className="vw-graf">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${L} ${A}`}
        width="100%" height="auto" style={{ display: 'block' }}
        role="img" aria-label="Evolução da adoção por loja"
        onMouseMove={aoMover}
        onMouseLeave={() => setAtivo(null)}
      >
        <defs>
          <linearGradient id="vwGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--vw-linha)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--vw-linha)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0, 25, 50, 75, 100].map((v) => (
          <g key={v}>
            <line x1={padE} y1={y(v)} x2={L - padD} y2={y(v)} stroke="var(--vw-grade)" strokeWidth="1" />
            <text x={padE - 6} y={y(v) + 3} textAnchor="end" className="vw-eixo">{v}%</text>
          </g>
        ))}

        {ativo !== null && (
          <line x1={x(ativo)} y1={padT} x2={x(ativo)} y2={A - padB}
            stroke="var(--vw-cursor)" strokeWidth="1" strokeDasharray="3 3" />
        )}

        {!multi && <path d={area} fill="url(#vwGrad)" />}

        {ordenadas.map((l) => (
          <path key={l.filial} d={l.d} fill="none"
            stroke={l.emFoco ? l.cor : CINZA}
            strokeWidth={l.emFoco && temFoco ? 2.6 : 1.7}
            strokeLinejoin="round" strokeLinecap="round"
            opacity={l.emFoco ? 0.95 : 0.28} />
        ))}

        {/* O total fica por cima, como referência do conjunto. */}
        <path d={linhaTotal} fill="none" stroke="var(--vw-total)"
          strokeWidth={multi ? 2.2 : 2} strokeLinejoin="round" strokeLinecap="round"
          strokeDasharray={multi ? '5 4' : '0'}
          opacity={temFoco ? 0.45 : 1} />

        {serie.map((m, i) => (
          m.adocao !== null && (
            <circle key={m.mes} cx={x(i)} cy={y(m.adocao)} r={m.parcial ? 4 : 2.6}
              fill={m.parcial ? 'var(--vw-fundo)' : 'var(--vw-total)'}
              stroke="var(--vw-total)" strokeWidth={m.parcial ? 2 : 0}
              opacity={temFoco ? 0.45 : 1} />
          )
        ))}

        {serie.map((m, i) => (
          (i % 2 === 0 || i === serie.length - 1) && (
            <text key={`r${m.mes}`} x={x(i)} y={A - 6} textAnchor="middle" className="vw-eixo">
              {m.mes.slice(5)}/{m.mes.slice(2, 4)}
            </text>
          )
        ))}
      </svg>

      {ativo !== null && noMes.length > 0 && (
        <div className={`vw-tip ${x(ativo) > L / 2 ? 'vw-tip--esq' : ''}`}>
          <div className="vw-tip__mes">
            {serie[ativo].mes}
            {serie[ativo].parcial && <span className="vw-tip__parcial">mês em curso</span>}
          </div>
          {!temFoco && (
            <div className="vw-tip__total">
              Total {pct(serie[ativo].adocao)}
              <span>{num(serie[ativo].novo)} de {num(serie[ativo].total)} pedidos</span>
            </div>
          )}
          {noMes.map((p) => (
            <div className="vw-tip__linha" key={p.nome}>
              <span className="vw-tip__cor" style={{ background: p.cor }} />
              <span className="vw-tip__loja">{p.nome}</span>
              <span className="vw-tip__pct">{pct(p.adocao)}</span>
              <span className="vw-tip__qtd">{num(p.novo)}/{num(p.novo + p.legado)}</span>
            </div>
          ))}
        </div>
      )}

      {multi && (
        <div className="vw-chips">
          {linhas.map((l) => {
            const marcada = focadas.has(l.filial);
            return (
              <button
                type="button"
                key={l.filial}
                className={`vw-chip ${marcada ? 'vw-chip--on' : ''} ${temFoco && !marcada ? 'vw-chip--off' : ''}`}
                style={marcada ? { '--cor': l.cor } : undefined}
                aria-pressed={marcada}
                onClick={() => alternar(l.filial)}
                title={marcada ? `Tirar ${l.nome} do destaque` : `Destacar ${l.nome}`}
              >
                <span className="vw-chip__cor" style={{ background: temFoco && !marcada ? CINZA : l.cor }} />
                {l.nome}
              </button>
            );
          })}
          {temFoco && (
            <button type="button" className="vw-chip vw-chip--limpar" onClick={() => setFocadas(new Set())}>
              limpar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Uma linha por loja. Tudo em colunas fixas para os numeros alinharem entre
// si — antes "3.015 pedidos · 16 vend." era uma string so, e cada linha
// terminava num lugar diferente.
function BarraLoja({ f }) {
  const e = ESTAGIO[f.estagio] || ESTAGIO['sem-pedidos'];
  return (
    <div className="vw-loja">
      <span className="vw-loja__nome" title={`Filial ${f.filial}`}>{f.nome}</span>
      <div className="vw-loja__trilho">
        <div className="vw-loja__preenchido" style={{ width: `${f.adocao ?? 0}%`, background: e.cor }} />
      </div>
      <span className="vw-loja__pct">{pct(f.adocao)}</span>
      <span className="vw-loja__etiqueta" style={{ '--cor': e.cor }}>{e.rotulo}</span>
      <span className="vw-loja__num">{num(f.total)}</span>
      <span className="vw-loja__num">{f.vendedores}</span>
    </div>
  );
}

function ListaVendedores({ titulo, ajuda, itens, campo, corBarra }) {
  // A ultima coluna e sempre a adocao DA PESSOA (quanto do que ela lanca ja vai
  // no novo). Na lista da direita isso confunde sem rotulo: um vendedor pode
  // aparecer nas duas listas, com o mesmo percentual.
  const maximo = Math.max(1, ...itens.map((v) => v[campo]));
  return (
    <div className="card h-100">
      <div className="card-body">
        <h6 className="mb-1">{titulo}</h6>
        <p className="text-muted small mb-3">{ajuda}</p>
        {!itens.length ? <p className="text-muted small mb-0">Nada no período.</p> : (
          <div className="vw-rank">
            <div className="vw-rank__cabecalho">
              <span>vendedor</span>
              <span />
              <span>qtd</span>
              <span>adoção</span>
            </div>
            {itens.map((v) => (
              <div className="vw-rank__linha" key={v.codvend}>
                <span className="vw-rank__nome" title={`${v.codvend} — ${v.filialNome}`}>
                  {v.nome} <span className="text-muted">· {v.filialNome}</span>
                </span>
                <div className="vw-rank__trilho">
                  <div className="vw-rank__preenchido"
                    style={{ width: `${(100 * v[campo]) / maximo}%`, background: corBarra }} />
                </div>
                <span className="vw-rank__valor">{num(v[campo])}</span>
                <span className="vw-rank__pct">{pct(v.adocao)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function VendasWeb() {
  const [dados, setDados] = useState(null);
  const [dias, setDias] = useState(30);
  const [filial, setFilial] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await axios.get(`${API}/vendas-web/painel`, {
        params: { dias, meses: 12, filial: filial || undefined },
      });
      setDados(data?.data || null);
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível carregar o painel.');
    } finally {
      setCarregando(false);
    }
  }, [dias, filial]);

  useEffect(() => { carregar(); }, [carregar]);

  if (carregando && !dados) {
    return <div className="p-4"><div className="spinner-border" role="status" /></div>;
  }

  const k = dados?.kpis;
  const variacao = k?.variacaoMes;

  return (
    <div className="container-fluid py-3 vw">
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h4 className="mb-1">Adoção do {NOVO}</h4>
          <p className="text-muted mb-0" style={{ maxWidth: '62ch' }}>
            Quanto da operação já lança pedido na plataforma nova e quanto ainda usa
            o {LEGADO}. Conta pedidos por origem — não é volume de venda.
          </p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <select className="form-select form-select-sm" style={{ width: 'auto' }}
            value={filial} onChange={(e) => setFilial(e.target.value)}>
            <option value="">Todas as filiais</option>
            {(dados?.filiais || []).map((f) => (
              <option key={f.filial} value={f.filial}>{f.filial} · {f.nome}</option>
            ))}
          </select>
          <div className="btn-group btn-group-sm" role="group">
            {[7, 30, 90].map((d) => (
              <button key={d} type="button"
                className={`btn ${dias === d ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setDias(d)}>{d}d</button>
            ))}
          </div>
        </div>
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      {k && (
        <>
          <div className="vw-kpis mb-4">
            <div className="vw-kpi vw-kpi--destaque">
              <span className="vw-kpi__rotulo">Adoção em {dados.janelaDias} dias</span>
              <strong className="vw-kpi__valor">{pct(k.adocao)}</strong>
              {variacao !== null && variacao !== undefined && (
                <span className={`vw-kpi__delta ${variacao >= 0 ? 'vw-sobe' : 'vw-desce'}`}>
                  {variacao >= 0 ? '▲' : '▼'} {Math.abs(variacao).toFixed(1)} pts em {k.mesComparado}
                </span>
              )}
            </div>
            <div className="vw-kpi">
              <span className="vw-kpi__rotulo">{NOVO}</span>
              <strong className="vw-kpi__valor">{num(k.novo)}</strong>
              <span className="vw-kpi__det">pedidos</span>
            </div>
            <div className="vw-kpi">
              <span className="vw-kpi__rotulo">{LEGADO}</span>
              <strong className="vw-kpi__valor">{num(k.legado)}</strong>
              <span className="vw-kpi__det">pedidos</span>
            </div>
            <div className="vw-kpi">
              <span className="vw-kpi__rotulo">Lojas migradas</span>
              <strong className="vw-kpi__valor">{k.lojasMigradas}<span className="vw-kpi__de">/{k.lojasTotal}</span></strong>
              <span className="vw-kpi__det">acima de 90%</span>
            </div>
            <div className="vw-kpi">
              <span className="vw-kpi__rotulo">Vendedores ativos</span>
              <strong className="vw-kpi__valor">{k.vendedoresNoNovo}<span className="vw-kpi__de">/{k.vendedoresTotal}</span></strong>
              <span className="vw-kpi__det">já usaram o novo</span>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-baseline mb-2">
                <h6 className="mb-0">Evolução mensal</h6>
                <span className="text-muted small">12 meses · passe o mouse para ver loja a loja</span>
              </div>
              <Evolucao serie={dados.serie} seriesPorLoja={dados.seriesPorLoja} />
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-baseline mb-3">
                <h6 className="mb-0">Adoção por loja</h6>
                <span className="text-muted small">últimos {dados.janelaDias} dias</span>
              </div>
              <div className="vw-loja vw-loja--cabecalho">
                <span>loja</span>
                <span />
                <span>adoção</span>
                <span>estágio</span>
                <span>pedidos</span>
                <span>vend.</span>
              </div>
              {dados.filiais.map((f) => <BarraLoja key={f.filial} f={f} />)}
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-xl-6">
              <ListaVendedores
                titulo={`Quem mais usa o ${NOVO}`}
                ajuda="Pedidos lançados na plataforma nova no período."
                itens={dados.topAdotantes} campo="novo" corBarra="var(--vw-ok)" />
            </div>
            <div className="col-12 col-xl-6">
              <ListaVendedores
                titulo={`Ainda no ${LEGADO}`}
                ajuda="Maior volume ainda no antigo — é onde o próximo passo do rollout rende mais."
                itens={dados.aindaNoLegado} campo="legado" corBarra="var(--vw-parado)" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
